import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'

import type { DriverRemittanceStatus } from '@/lib/api/driver-cash-api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    useCreateRemittance,
    useMyDriverCash,
    useRemittances,
} from '@/hooks/use-driver-cash-query'
import { m } from '@/lib/paraglide/messages'
import { formatDateTimeShort, formatVnd } from '@/lib/format'

export const Route = createFileRoute('/_app/_authed/_sidebar/driver/cash')({
    component: DriverCashPage,
})

function statusLabel(status: DriverRemittanceStatus): string {
    switch (status) {
        case 'pending':
            return m.driverRemittance_status_pending()
        case 'confirmed':
            return m.driverRemittance_status_confirmed()
        case 'rejected':
            return m.driverRemittance_status_rejected()
        case 'voided':
            return m.driverRemittance_status_voided()
    }
}

function statusVariant(
    status: DriverRemittanceStatus,
): 'default' | 'secondary' | 'destructive' | 'outline' {
    switch (status) {
        case 'confirmed':
            return 'default'
        case 'pending':
            return 'secondary'
        case 'rejected':
            return 'destructive'
        case 'voided':
            return 'outline'
    }
}

function DriverCashPage() {
    const { data: summary, isLoading } = useMyDriverCash()
    const { data: remittances } = useRemittances({ per_page: 50 })
    const createRemittance = useCreateRemittance()

    const [open, setOpen] = useState(false)
    const [amount, setAmount] = useState('')
    const [note, setNote] = useState('')

    const handleSubmit = () => {
        const value = Number(amount)
        if (!Number.isFinite(value) || value <= 0) return
        createRemittance.mutate(
            { amount: value, note: note || undefined },
            {
                onSuccess: () => {
                    setOpen(false)
                    setAmount('')
                    setNote('')
                },
            },
        )
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-semibold">
                    {m.cashLedger_my_title()}
                </h1>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="open-remittance">
                            {m.driverRemittance_submit()}
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>
                                {m.driverRemittance_submit()}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3">
                            <div className="space-y-1">
                                <Label htmlFor="remittance-amount">
                                    {m.driverRemittance_amount()}
                                </Label>
                                <Input
                                    id="remittance-amount"
                                    type="number"
                                    inputMode="numeric"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                />
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor="remittance-note">
                                    {m.driverRemittance_note()}
                                </Label>
                                <Input
                                    id="remittance-note"
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button
                                data-testid="submit-remittance"
                                onClick={handleSubmit}
                                disabled={createRemittance.isPending}
                            >
                                {m.common_save()}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                            {m.cashLedger_collected()}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-lg font-semibold">
                            {formatVnd(summary?.collected ?? 0)}
                        </p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                            {m.cashLedger_remitted()}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-lg font-semibold">
                            {formatVnd(summary?.remitted ?? 0)}
                        </p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                            {m.cashLedger_held()}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p
                            className="text-lg font-semibold text-primary"
                            data-testid="held-amount"
                        >
                            {formatVnd(summary?.held ?? 0)}
                        </p>
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">
                        {m.driverRemittance_history()}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {isLoading ? (
                        <p className="text-sm text-muted-foreground">
                            {m.common_loading()}
                        </p>
                    ) : (remittances?.data.length ?? 0) === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            {m.driverRemittance_empty()}
                        </p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>
                                        {m.driverRemittance_col_date()}
                                    </TableHead>
                                    <TableHead>
                                        {m.driverRemittance_amount()}
                                    </TableHead>
                                    <TableHead>
                                        {m.driverRemittance_col_confirmed()}
                                    </TableHead>
                                    <TableHead>
                                        {m.driverRemittance_col_status()}
                                    </TableHead>
                                    <TableHead>
                                        {m.driverRemittance_col_note()}
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {remittances?.data.map((r) => (
                                    <TableRow key={r.id}>
                                        <TableCell>
                                            {formatDateTimeShort(r.created_at)}
                                        </TableCell>
                                        <TableCell>
                                            {formatVnd(r.amount)}
                                        </TableCell>
                                        <TableCell>
                                            {r.confirmed_amount != null
                                                ? formatVnd(r.confirmed_amount)
                                                : '—'}
                                        </TableCell>
                                        <TableCell>
                                            <Badge
                                                variant={statusVariant(
                                                    r.status,
                                                )}
                                            >
                                                {statusLabel(r.status)}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-xs text-muted-foreground">
                                            {r.note || '—'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}
